/**
 * BriefingCard — Begrüßungskarte mit dem persönlichen KI-Briefing (Jul-2026,
 * User-Wunsch): erscheint morgens oben im Händler-/Mitarbeiter-Dashboard.
 * Manager sehen die Org-Übersicht, Mitarbeiter eigene Aufgaben + Termine +
 * Filial-Zahlen (Backend: GET /api/assistant/briefing). 1x pro Tag ausblendbar.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sunrise, Sun, Moon, X, ArrowRight, Sparkles, Volume2, Square, Loader2, Settings, Trash2, AlertTriangle, Check } from "lucide-react";

import api from "../../lib/api";
import { toast } from "sonner";
import { useGearsVisible } from "../../lib/gearVisibility";
import BriefingSettingsModal from "./BriefingSettingsModal";

const todayKey = () => new Date().toISOString().slice(0, 10);

// Aug-2026 (User-Wunsch) — beim Vorlesen begrüßt Martin den User freundlich mit
// Namen, liest das Briefing vor und verabschiedet sich nett (wechselnde Floskeln).
const GREETINGS = {
    morning: [
        (n) => `Guten Morgen${n}! Schön, dass du da bist. Hier ist dein Briefing für heute:`,
        (n) => `Einen wunderschönen guten Morgen${n}! Ich habe dein Briefing vorbereitet:`,
        (n) => `Moin${n}! Auf geht's in den Tag — das steht heute an:`,
        (n) => `Guten Morgen${n}, ich hoffe, du hattest einen guten Start. Hier dein Überblick:`,
    ],
    day: [
        (n) => `Hallo${n}! Schön, dich zu sehen. Hier ist dein aktuelles Briefing:`,
        (n) => `Guten Tag${n}! Ich habe die wichtigsten Punkte für dich zusammengefasst:`,
        (n) => `Hi${n}! Kurzer Überblick gefällig? Das steht gerade an:`,
    ],
    evening: [
        (n) => `Guten Abend${n}! Schön, dass du noch reinschaust. Hier dein Briefing:`,
        (n) => `Hallo${n}, einen schönen Abend! Das ist der aktuelle Stand:`,
    ],
};
const FAREWELLS = [
    "Ich wünsche dir einen richtig erfolgreichen Tag — bis später!",
    "Viel Erfolg heute, du schaffst das! Bis bald.",
    "Das war's von mir — hab einen großartigen Tag!",
    "Bis später, und lass es dir gut gehen!",
    "Ich bin da, wenn du mich brauchst. Einen schönen Tag dir!",
];
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

export default function BriefingCard({ user }) {
    const [dismissed, setDismissed] = useState(
        () => localStorage.getItem("tw_briefing_dismissed") === todayKey());
    const [data, setData] = useState(null);
    // Jul-2026 (User-Wunsch) — Zahnrad: Briefing-Bausteine konfigurieren.
    const gearsOk = useGearsVisible();
    const [settingsOpen, setSettingsOpen] = useState(false);
    // Sep-2026 (User-Wunsch) — „Aufräumen" aufgelaufener Daten (nur Hauptsitz).
    const isHq = user?.role === "b2b" && !user?.hauptsitz_id;
    const [resetOpen, setResetOpen] = useState(false);
    const [resetAreas, setResetAreas] = useState({ tasks: true, leads: true, emails: true });
    const [resetScope, setResetScope] = useState("old");
    const [resetDays, setResetDays] = useState(30);
    const [resetPreview, setResetPreview] = useState(null);
    const [resetBusy, setResetBusy] = useState(false);
    const [resetConfirm, setResetConfirm] = useState("");
    const [archived, setArchived] = useState(null);
    useEffect(() => {
        if (!resetOpen) return;
        const areas = Object.entries(resetAreas).filter(([, v]) => v).map(([k]) => k);
        if (areas.length) {
            api.post("/dealer/dashboard/reset", { areas, scope: resetScope, days: Number(resetDays) || 30, dry_run: true })
                .then((r) => setResetPreview(r.data)).catch(() => setResetPreview(null));
        } else { setResetPreview(null); }
        api.post("/dealer/dashboard/restore", { dry_run: true }).then((r) => setArchived(r.data)).catch(() => setArchived(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetOpen, resetScope, resetDays, resetAreas.tasks, resetAreas.leads, resetAreas.emails]);
    // Jul-2026 (User-Wunsch) — Briefing-Vorlesen: Martin liest das Briefing vor (OpenAI TTS).
    const [ttsState, setTtsState] = useState("idle"); // idle | loading | playing | asking
    const audioRef = useRef(null);
    // Jul-2026 (User-Wunsch): automatisch vorlesen bei Maus-Hover (1x pro Tag),
    // nach dem Vorlesen ausblenden — bis am nächsten Morgen neue Meldungen kommen.
    const hoverFired = useRef(false);
    const dismissTimer = useRef(null);
    // Jul-2026 (User-Wunsch) — Auto-Play beim ersten App-Öffnen des Tages (v. a.
    // Handy). Blockiert der Browser das Autoplay (keine Nutzer-Geste), pulsiert
    // der Anhören-Knopf als Einladung.
    const autoTried = useRef(false);
    const [autoBlocked, setAutoBlocked] = useState(false);

    useEffect(() => {
        if (dismissed) return;
        api.get("/assistant/briefing").then((r) => setData(r.data)).catch(() => {});
        return () => { audioRef.current?.pause(); clearTimeout(dismissTimer.current); };
    }, [dismissed]);

    useEffect(() => {
        if (!data?.text || dismissed || autoTried.current) return;
        if (localStorage.getItem(`tw_briefing_autoplay_${todayKey()}`)) return;
        autoTried.current = true;
        speak(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const playText = async (text, onEnd) => {
        const r = await api.post("/assistant/tts", { text });
        const audio = new Audio(`data:${r.data.mime || "audio/mp3"};base64,${r.data.audio_base64}`);
        audioRef.current = audio;
        audio.onended = onEnd || (() => setTtsState("idle"));
        await audio.play();
    };

    const speak = async (auto = false) => {
        if (ttsState !== "idle") {
            audioRef.current?.pause();
            audioRef.current = null;
            clearTimeout(dismissTimer.current);
            setTtsState("idle");
            return;
        }
        setAutoBlocked(false);
        setTtsState("loading");
        // Aug-2026 (User-Wunsch): freundliche Begrüßung mit Namen + nette
        // Verabschiedung — mit wechselnden Floskeln je Tageszeit.
        const hr = new Date().getHours();
        const namePart = firstName ? `, ${firstName}` : "";
        const greet = pick(hr < 11 ? GREETINGS.morning : hr < 18 ? GREETINGS.day : GREETINGS.evening)(namePart);
        const farewell = pick(FAREWELLS);
        try {
            const chips = (data.items || []).filter((i) => i.count > 0);
            await playText(
                chips.length ? `${greet} ${data.text}` : `${greet} ${data.text} ${farewell}`,
                async () => {
                    if (!chips.length) {
                        // Nichts zu tun → nach Vorlesen + Verabschiedung ausblenden.
                        setTtsState("idle");
                        dismiss();
                        return;
                    }
                    // Jul-2026 (User-Wunsch) — Briefing-Antwort: Martin fragt direkt nach.
                    try {
                        setTtsState("asking");
                        await playText("Womit soll ich anfangen?", () => {
                            // Zeit zum Klicken lassen, dann nett verabschieden + ausblenden.
                            dismissTimer.current = setTimeout(async () => {
                                try { await playText(farewell, () => { setTtsState("idle"); dismiss(); }); }
                                catch { dismiss(); }
                            }, 12000);
                        });
                    } catch { setTtsState("idle"); }
                });
            setTtsState("playing");
            localStorage.setItem(`tw_briefing_autoplay_${todayKey()}`, "1");
        } catch {
            setTtsState("idle");
            if (auto === true) setAutoBlocked(true); // Autoplay blockiert → Knopf pulsiert
        }
    };

    const onHover = () => {
        if (hoverFired.current || ttsState !== "idle") return;
        const key = `tw_briefing_autoplay_${todayKey()}`;
        if (localStorage.getItem(key)) return;
        localStorage.setItem(key, "1");
        hoverFired.current = true;
        speak();
    };

    if (dismissed || !data?.text) return null;

    const h = new Date().getHours();
    const greeting = h < 11 ? "Guten Morgen" : h < 18 ? "Guten Tag" : "Guten Abend";
    const GreetIcon = h < 11 ? Sunrise : h < 18 ? Sun : Moon;
    const buildAreas = () => Object.entries(resetAreas).filter(([, v]) => v).map(([k]) => k);
    const doReset = async () => {
        const areas = buildAreas();
        if (!areas.length) return;
        setResetBusy(true);
        try {
            const r = await api.post("/dealer/dashboard/reset",
                { areas, scope: resetScope, days: Number(resetDays) || 30, dry_run: false });
            const n = (r.data.tasks || 0) + (r.data.leads || 0) + (r.data.emails || 0);
            toast.success(`Aufgeräumt: ${n} Einträge archiviert (30 Tage wiederherstellbar).`);
            setResetOpen(false); setResetConfirm("");
            const b = await api.get("/assistant/briefing"); setData(b.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Zurücksetzen fehlgeschlagen."); }
        finally { setResetBusy(false); }
    };
    const doRestore = async () => {
        setResetBusy(true);
        try {
            const r = await api.post("/dealer/dashboard/restore", { areas: buildAreas(), dry_run: false });
            const n = (r.data.tasks || 0) + (r.data.leads || 0) + (r.data.emails || 0);
            toast.success(`${n} Einträge wiederhergestellt.`);
            setResetOpen(false);
            const b = await api.get("/assistant/briefing"); setData(b.data);
        } catch (e) { toast.error(e.response?.data?.detail || "Wiederherstellen fehlgeschlagen."); }
        finally { setResetBusy(false); }
    };
    const firstName = (user?.first_name || (user?.name || "").split(" ")[0] || "").trim();
    const chips = (data.items || []).filter((i) => i.count > 0).sort((a, b) => b.count - a.count);

    const dismiss = () => {
        localStorage.setItem("tw_briefing_dismissed", todayKey());
        setDismissed(true);
    };

    return (
        <div className="relative mb-6 bg-[#111827] text-white rounded-sm border-l-4 border-[#FFCC00] px-5 py-4 overflow-hidden"
            onMouseEnter={onHover}
            data-testid="briefing-card">
            <div className="absolute top-2.5 right-2.5 flex items-center gap-2">
                {isHq && (
                    <button onClick={() => setResetOpen(true)}
                        title="Aufgelaufene Daten aufräumen (Aufgaben, Leads, E-Mail-Freigabe)"
                        className="text-white/40 hover:text-red-400 transition-colors"
                        data-testid="briefing-reset-open">
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
                {gearsOk && (
                    <button onClick={() => setSettingsOpen(true)}
                        title="Briefing-Bausteine konfigurieren (wer sieht was)"
                        className="text-white/40 hover:text-[#FFCC00] transition-colors"
                        data-testid="briefing-settings-gear">
                        <Settings className="w-4 h-4" />
                    </button>
                )}
                <button onClick={() => speak()}
                    title={ttsState === "idle" ? "Briefing vorlesen lassen" : "Vorlesen stoppen"}
                    className={`transition-colors ${ttsState === "idle" ? "text-white/40 hover:text-[#FFCC00]" : "text-[#FFCC00]"} ${autoBlocked ? "text-[#FFCC00] animate-pulse" : ""}`}
                    data-testid="briefing-card-tts">
                    {ttsState === "loading" ? <Loader2 className="w-4 h-4 animate-spin" />
                        : ttsState === "playing" ? <Square className="w-4 h-4" />
                            : <Volume2 className="w-4 h-4" />}
                </button>
                <button onClick={dismiss} title="Für heute ausblenden"
                    className="text-white/40 hover:text-white"
                    data-testid="briefing-card-dismiss">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="flex items-center gap-2 text-[#FFCC00] text-xs font-black uppercase tracking-wider">
                <GreetIcon className="w-4 h-4" /> {greeting}{firstName ? `, ${firstName}` : ""}
                <span className="inline-flex items-center gap-1 text-white/40 normal-case font-bold tracking-normal">
                    <Sparkles className="w-3 h-3" /> Dein Briefing
                </span>
            </div>
            <p className="text-sm text-white/85 mt-1.5 leading-relaxed max-w-3xl" data-testid="briefing-card-text">
                {data.text}
            </p>
            {autoBlocked && (
                <button onClick={() => speak()}
                    className="mt-2 inline-flex items-center gap-1.5 bg-[#FFCC00] text-[#111827] text-xs font-black rounded-sm px-3 py-1.5 animate-pulse hover:animate-none hover:bg-yellow-300 transition-colors"
                    data-testid="briefing-autoplay-hint">
                    <Volume2 className="w-3.5 h-3.5" /> Briefing anhören
                </button>
            )}
            {chips.length > 0 && (
                <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="briefing-card-chips">
                    {ttsState === "asking" && (
                        <span className="text-xs font-bold text-[#FFCC00] animate-pulse" data-testid="briefing-ask-prompt">
                            Womit soll ich anfangen? →
                        </span>
                    )}
                    {chips.map((i, idx) => (
                        <Link key={i.key} to={i.to || "/dashboard"}
                            className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1.5 rounded-sm transition-colors ${ttsState === "asking" && idx === 0 ? "bg-[#FFCC00] text-[#111827] animate-pulse" : "bg-white/10 hover:bg-[#FFCC00] hover:text-[#111827]"}`}
                            data-testid={`briefing-chip-${i.key}`}>
                            <span className="font-mono-num font-black">{i.count}</span> {i.label}
                            <ArrowRight className="w-3 h-3" />
                        </Link>
                    ))}
                </div>
            )}
            {settingsOpen && <BriefingSettingsModal onClose={() => setSettingsOpen(false)} />}
            {resetOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="briefing-reset-modal" onClick={() => !resetBusy && setResetOpen(false)}>
                    <div className="bg-white text-[#111827] rounded-sm w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                            <h3 className="font-display font-black text-base flex items-center gap-2"><Trash2 className="w-4 h-4 text-red-500" /> Aufgelaufene Daten aufräumen</h3>
                            <button onClick={() => setResetOpen(false)} className="text-gray-400 hover:text-gray-900" data-testid="briefing-reset-close"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="px-4 py-3 space-y-3 text-sm">
                            <p className="text-xs text-gray-500">Wird <b>archiviert</b> (nicht endgültig gelöscht) und ist 30 Tage wiederherstellbar. Gilt organisationsweit für alle Standorte.</p>
                            <div className="space-y-1.5">
                                {[["tasks", "Team-Aufgaben"], ["leads", "Leads"], ["emails", "E-Mails in der Freigabe"]].map(([k, lbl]) => (
                                    <label key={k} className="flex items-center gap-2 cursor-pointer" data-testid={`briefing-reset-area-${k}`}>
                                        <input type="checkbox" checked={resetAreas[k]} onChange={(e) => setResetAreas((p) => ({ ...p, [k]: e.target.checked }))} className="accent-[#FFCC00]" />
                                        <span className="font-semibold">{lbl}</span>
                                        {resetPreview && <span className="ml-auto font-mono-num text-gray-500">{resetPreview[k] ?? 0}</span>}
                                    </label>
                                ))}
                            </div>
                            <div className="flex items-center gap-3 pt-1">
                                <label className="flex items-center gap-1.5 cursor-pointer"><input type="radio" name="rscope" checked={resetScope === "old"} onChange={() => setResetScope("old")} data-testid="briefing-reset-scope-old" /> älter als
                                    <input type="number" min={1} value={resetDays} onChange={(e) => setResetDays(e.target.value)} disabled={resetScope !== "old"} className="w-14 border rounded-sm px-1.5 py-0.5" data-testid="briefing-reset-days" /> Tage</label>
                                <label className="flex items-center gap-1.5 cursor-pointer text-red-700 font-bold"><input type="radio" name="rscope" checked={resetScope === "all"} onChange={() => setResetScope("all")} data-testid="briefing-reset-scope-all" /> ALLES</label>
                            </div>
                            <div className="rounded-sm bg-gray-50 border px-3 py-2 flex items-center gap-2" data-testid="briefing-reset-preview">
                                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                                <span>Es werden <b className="font-mono-num">{resetPreview ? ((resetPreview.tasks || 0) + (resetPreview.leads || 0) + (resetPreview.emails || 0)) : "…"}</b> Einträge archiviert.</span>
                            </div>
                            {resetScope === "all" && (
                                <input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="Zum Bestätigen LÖSCHEN eingeben" className="w-full border-2 border-red-300 rounded-sm px-2 py-1.5" data-testid="briefing-reset-confirm-input" />
                            )}
                        </div>
                        <div className="flex justify-end gap-2 px-4 py-3 border-t">
                            {archived && ((archived.tasks || 0) + (archived.leads || 0) + (archived.emails || 0) > 0) && (
                                <button onClick={doRestore} disabled={resetBusy} className="mr-auto text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1" data-testid="briefing-restore-btn" title="Zuletzt archivierte Einträge (letzte 30 Tage) zurückholen">
                                    ↩ {(archived.tasks || 0) + (archived.leads || 0) + (archived.emails || 0)} wiederherstellen
                                </button>
                            )}
                            <button onClick={() => setResetOpen(false)} className="text-sm font-bold text-gray-500 px-3 py-1.5" data-testid="briefing-reset-cancel">Abbrechen</button>
                            <button onClick={doReset}
                                disabled={resetBusy || !buildAreas().length || (resetScope === "all" && resetConfirm !== "LÖSCHEN")}
                                className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-black rounded-sm px-4 py-1.5"
                                data-testid="briefing-reset-submit">
                                {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Aufräumen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
